import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
// The icon set holds no straight-up arrow, so the back arrow is turned to point up.
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow-left.svg';
import IconButton from 'components/IconButton/IconButton';
import aiScreenLevels from 'utils/aiScreenLevels';

import { MESSAGE_MAX_LENGTH } from '../constants/message';

import type {
  ChangeEvent, FormEvent, KeyboardEvent, ReactElement,
} from 'react';
import type IAiChatComposer from '../interfaces/IAiChatComposer';
import type { IAiChatScreenLevel } from '../interfaces/IAiChatScreen';

import styles from '../sass/AiChat.module.scss';

/** Message box: Enter sends, Shift+Enter starts a new line. */
/**
 * Keys a level by the levels under it. A stack has no ids, and the same name can be open twice —
 * a wizard step that opens itself again — while the path down to a level is its own.
 */
const levelKey = (levels: IAiChatScreenLevel[], index: number): string => levels
  .slice(0, index + 1)
  .map((level) => level.name)
  .join('›');

function AiChatComposer({
  onSend, isDisabled, isStreaming, screen,
}: IAiChatComposer): ReactElement {
  const { t } = useTranslation();
  const [text, setText] = useState<string>('');
  // Only the names are shown. The rest of what each level declared is held for what it is sent
  // with, not for the reader.
  const levels = useMemo(() => (screen ? aiScreenLevels(screen) : []), [screen]);
  const trimmed = text.trim();
  const canSend = !!trimmed && !isDisabled;

  const submit = () => {
    if (!canSend) {
      return;
    }

    setText('');
    onSend(trimmed);
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => setText(event.target.value);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  // The one mark the button carries today. `isStreaming` is the state a mark of its own is meant
  // for — a stop control while a reply is arriving — and this is the line to branch on it.
  const sendIcon = <ArrowIcon className={styles.sendIcon} />;

  return (
    <form className={styles.composer} onSubmit={handleSubmit}>
      {/* One chip per level, the screen first and each sub-screen open over it after. Rendered as
          text rather than through i18next, which escapes what it interpolates and would show the
          quotes around a name as entities. */}
      {levels.length ? (
        <div className={styles.context}>
          {levels.map((level, index) => (
            <Fragment key={levelKey(levels, index)}>
              {index ? <span className={styles.contextSeparator} aria-hidden>›</span> : null}
              <span className={styles.contextChip} title={level.url}>
                {level.name}
              </span>
            </Fragment>
          ))}
        </div>
      ) : null}

      <div className={styles.composerRow}>
        <textarea
          className={styles.input}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={t('aiChat.placeholder')}
          aria-label={t('aiChat.messageLabel')}
          rows={1}
          maxLength={MESSAGE_MAX_LENGTH}
          disabled={isDisabled}
        />
        <IconButton
          icon={sendIcon}
          onClick={submit}
          disabled={!canSend}
          ariaLabel={isStreaming ? t('aiChat.sending') : t('aiChat.send')}
        />
      </div>
    </form>
  );
}

export default AiChatComposer;
